import { useMemo } from "react";
import { Flame, Copy, CalendarClock, MoonStar, Sword, ChevronRight, X, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * HeroInsightBanner — surfaces the single most-actionable audit insight on the
 * dashboard so users don't have to open Deploy Shield to see it.
 *
 * Priority order:
 *   1. `drain`      — biggest recurring charge
 *   2. `duplicate`  — cheapest-wins reclaim
 *   3. `upcoming`   — charge in <=7 days
 *   4. `inactive`   — long-cycle forgotten subs
 *
 * Props:
 *   - insights: array from /api/audit/start
 *   - subs: full list of currently-active subs (to hydrate the "Defeat now" CTA)
 *   - onDefeat(id, sub): called when hero taps "Defeat now"
 *   - onDismiss(kind): called when hero taps the X
 *   - dismissedKinds: Set<string> of already-dismissed kinds (persisted in localStorage)
 */
const PRIORITY = ["drain", "duplicate", "upcoming", "inactive"];

const META = {
  drain: {
    Icon: Flame,
    accent: "from-red-500 to-red-600",
    chip: "bg-red-100 text-red-700",
    eyebrow: "Biggest drain",
  },
  duplicate: {
    Icon: Copy,
    accent: "from-amber-500 to-orange-500",
    chip: "bg-amber-100 text-amber-800",
    eyebrow: "Overlapping subs",
  },
  upcoming: {
    Icon: CalendarClock,
    accent: "from-sky-500 to-blue-600",
    chip: "bg-sky-100 text-sky-700",
    eyebrow: "Renews soon",
  },
  inactive: {
    Icon: MoonStar,
    accent: "from-slate-600 to-slate-800",
    chip: "bg-slate-100 text-slate-700",
    eyebrow: "Easy to forget",
  },
};

export default function HeroInsightBanner({ insights, subs, onDefeat, onDismiss, dismissedKinds }) {
  const top = useMemo(() => {
    if (!insights?.length) return null;
    for (const kind of PRIORITY) {
      if (dismissedKinds?.has(kind)) continue;
      const ins = insights.find((i) => i.kind === kind);
      if (ins) return ins;
    }
    return null;
  }, [insights, dismissedKinds]);

  if (!top) return null;

  const meta = META[top.kind] || META.inactive;
  const Icon = meta.Icon;
  const primarySubId = top.sub_ids?.[0];
  const primarySub = subs?.find((s) => s.id === primarySubId) || null;
  const canDefeat = !!primarySub && (top.kind === "drain" || top.kind === "duplicate" || top.kind === "inactive");

  return (
    <div className="mt-6 relative" data-testid="hero-insight-banner" data-insight-kind={top.kind}>
      <div
        className={`relative rounded-3xl p-[2px] bg-gradient-to-br ${meta.accent} shadow-lg`}
      >
        <div className="relative rounded-[22px] bg-white px-4 pt-4 pb-3.5">
          {/* Absolute X so its hit area never collides with the message text */}
          <button
            type="button"
            onClick={() => onDismiss?.(top.kind)}
            className="absolute top-2 right-2 w-9 h-9 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-all z-10"
            data-testid="insight-banner-dismiss"
            aria-label="Dismiss insight"
          >
            <X className="w-4 h-4" strokeWidth={2.6} />
          </button>

          <div className="flex items-start gap-3 pr-9">
            <div
              className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${meta.accent} text-white flex items-center justify-center flex-shrink-0 shadow-md`}
            >
              <Icon className="w-5 h-5" strokeWidth={2.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.14em] ${meta.chip}`}
                  data-testid="insight-banner-eyebrow"
                >
                  {meta.eyebrow}
                </span>
                {top.monthly_impact > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-700" data-testid="insight-banner-savings">
                    <TrendingDown className="w-3 h-3" strokeWidth={3} />
                    ${top.monthly_impact.toFixed(2)}/mo back
                  </span>
                )}
              </div>
              <h3
                className="mt-1 text-base font-black text-slate-900 leading-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="insight-banner-title"
              >
                {top.title}
              </h3>
              <p className="mt-0.5 text-[12.5px] leading-snug text-slate-600 font-medium" data-testid="insight-banner-message">
                {top.message}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-3 flex items-center gap-2">
            {canDefeat ? (
              <Button
                size="sm"
                onClick={() => onDefeat?.(primarySub.id, primarySub)}
                className="h-9 flex-1 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold gap-1.5 px-3"
                data-testid="insight-banner-action"
              >
                <Sword className="w-4 h-4" strokeWidth={2.8} />
                Defeat {primarySub?.name ? `· ${primarySub.name}` : "now"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDismiss?.(top.kind)}
                className="h-9 flex-1 rounded-xl text-slate-700 hover:bg-slate-100 font-extrabold gap-1.5 px-3"
                data-testid="insight-banner-action"
              >
                Got it <ChevronRight className="w-4 h-4" strokeWidth={2.8} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
